import {
  useWSSnackbar,
  WSButton,
  WSContainer,
  WSFlexBox,
  WSGridCentered,
  WSPanel,
  WSRadioInputGroup,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../../components/BrowserPageTitle/BrowserPageTitle";
import { ProjectOnboardingLayout } from "../../../../components/ProjectOnboardingLayout/ProjectOnboardingLayout";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useCollaboratorsQuery } from "../../../../query/payments/queries";
import { WSQueries } from "../../../../query/WSQuery";
import { useBulkUploadPayablesImporter } from "./bulkUploadPayablesImporter";
import { PayableStatus, UploadPayable } from "./BulkUploadPayablesInfo";

export interface BulkUploadPayablesStatusProps {}

export const BulkUploadPayablesStatus: React.FC<BulkUploadPayablesStatusProps> = () => {
  useBrowserPageTitle("Wingspan - Bulk upload payables");
  const { openSnackbar } = useWSSnackbar();
  const [status, setStatus] = useState(PayableStatus.Draft);
  const [isFlatfileLoading, setIsFlatfileLoading] = useState(false);
  const history = useHistory();
  const collaboratorsQuery = useCollaboratorsQuery();
  const featureFlagsQuery = useFeatureFlags();

  const bulkUploadPayablesImporter = useBulkUploadPayablesImporter({
    payableStatus: status,
    onClose: () => {
      setIsFlatfileLoading(false);
    }
  });

  const importData = async () => {
    const results = await bulkUploadPayablesImporter();
    const lineItems = results.validData as Array<UploadPayable>;
    if (lineItems.length === 0) {
      openSnackbar({
        duration: 5000,
        type: "warning",
        message: "Sorry, Looks like the uploaded spreadsheet is empty!"
      });
      return;
    }
    console.log("Line Items");
    console.log(JSON.stringify(lineItems));
    setIsFlatfileLoading(false);
    history.push({
      pathname: `/member/invoices/payables/bulk-upload/confirm`,
      state: {
        lineItems: lineItems,
        fileName: results.fileName,
        /**
         * The uuid of the batch assigned by Flatfile
         */
        batchId: results.batchId
      },
      search: `?status=${status}`
    });
  };

  const onUploadSpreadsheet = async () => {
    setIsFlatfileLoading(true);
    importData();
  };

  return (
    <ProjectOnboardingLayout progress={(100 / 3) * 2}>
      <WSContainer verticalPadding>
        <WSGridCentered M={8} S={6} XS={6}>
          <WSText.Label color="grey600" mt="S" mb="M" pt="S">
            2 of 3
          </WSText.Label>
          <WSText.Heading2 mb="S">
            Bulk upload payables with a spreadsheet
          </WSText.Heading2>
          <WSPanel>
            <WSQueries queries={{ collaboratorsQuery, featureFlagsQuery }}>
              {({ featureFlagsQuery: { data: featureFlags } }) => {
                return (
                  <>
                    <WSText.Heading4 mb="M">
                      Select status for imported payables
                    </WSText.Heading4>
                    <WSRadioInputGroup
                      mb="M"
                      name="status"
                      value={status}
                      onChange={(event: any) => {
                        setStatus(event.target?.value);
                      }}
                      options={[
                        {
                          value: PayableStatus.Draft,
                          label: (
                            <WSFlexBox direction="column">
                              <WSText.Heading4 mb="XS">
                                {PayableStatus.Draft}
                              </WSText.Heading4>
                              <WSText.LabelSmall>
                                Payable will be hidden to collaborators until it
                                is opened
                              </WSText.LabelSmall>
                            </WSFlexBox>
                          )
                        },
                        {
                          value: PayableStatus.Open,
                          label: (
                            <WSFlexBox direction="column">
                              <WSText.Heading4 mb="XS">
                                {PayableStatus.Open}
                              </WSText.Heading4>
                              <WSText.LabelSmall>
                                Collaborator can see payable, but it will not be
                                paid until it is approved
                              </WSText.LabelSmall>
                            </WSFlexBox>
                          )
                        },
                        {
                          value: PayableStatus.Approved,
                          label: (
                            <WSFlexBox direction="column">
                              <WSText.Heading4 mb="XS">
                                {PayableStatus.Approved}
                              </WSText.Heading4>
                              <WSText.LabelSmall>
                                Collaborator will automatically be paid on the
                                next payroll date
                              </WSText.LabelSmall>
                            </WSFlexBox>
                          )
                        },
                        ...(featureFlags.nec1099Filing
                          ? [
                              {
                                value: PayableStatus.Paid,
                                label: (
                                  <WSFlexBox direction="column">
                                    <WSText.Heading4 mb="XS">
                                      {PayableStatus.Paid} (Historical import)
                                    </WSText.Heading4>
                                    <WSText.LabelSmall>
                                      Payables will be visible to collaborators
                                      and marked as paid off of Wingspan
                                    </WSText.LabelSmall>
                                  </WSFlexBox>
                                )
                              }
                            ]
                          : [])
                      ]}
                    />
                    <WSButton
                      loading={isFlatfileLoading}
                      fullWidth
                      onClick={onUploadSpreadsheet}
                    >
                      Upload Payables
                    </WSButton>
                  </>
                );
              }}
            </WSQueries>
          </WSPanel>
        </WSGridCentered>
      </WSContainer>
    </ProjectOnboardingLayout>
  );
};
